// glonet-react-tailwind-project--src--components--WaterCoolerStory.js
// "A virtual meeting space" — the Water Cooler explainer, moved off the
// landing (owner direction 2026-08-11: the landing is one screen; anything
// that explains rather than converts gets its own page). Linked from the
// ⓘ line under "Enter the Water Cooler" and the nav. Full-bleed sibling of
// the landing, same treatment as /hosts.
//
// 2026-09-01: the page tells the CONNECTED story — The Eleven, the live
// portal graph (wc config 10): entry venue wired through the Portal Ring to
// ten more floors. The wiring map's node positions and door lists mirror
// e2e/eleven-graph.js; the floor shots are real renders from
// e2e/wc-template-shots.js (the SSR shot tool), not mockups.
//
// 2026-09-02 (owner feedback): the best parts were below the drab parts.
// Reordered so the floors lead — the nature loop is the first thing under
// the headline — and the wiring (hub, map) comes after you've seen where the
// doors go. The Venue shot was on the page twice and was the dullest image
// on it; it is gone (the Venue stays a node on the map and a line of copy).
// "Enter the Water Cooler" is ONE button now, in the sticky top bar, so it is
// on screen wherever the reader is instead of at the top and again 7000px
// later.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import shotPortalRing from "../assets/glonetai/wc-portal-ring.jpg";
import shotCascades from "../assets/glonetai/wc-riverside-cascades.jpg";
import shotSantorini from "../assets/glonetai/wc-santorini.jpg";
import shotSkyward from "../assets/glonetai/wc-skyward-isles.jpg";
import shotSakura from "../assets/glonetai/wc-sakura-garden.jpg";
import shotBoardroom from "../assets/glonetai/wc-glass-boardroom.jpg";
import shotLibrary from "../assets/glonetai/wc-library-stacks.jpg";
import shotSpeakeasy from "../assets/glonetai/wc-speakeasy.jpg";
import shotAbyss from "../assets/glonetai/wc-abyss-reef.jpg";
import shotCaverns from "../assets/glonetai/wc-crystal-caverns.jpg";
import api from "../services/api";
import { API_PATHS } from "../config/apiConfig";

const ACCENT = "#74a0f5";
const ACCENT_STRONG = "#5b8cf0";
const LIVE_DOT = "#57d9a4";
const MONO = "'JetBrains Mono',monospace";
const SERIF = "'Instrument Serif',serif";

// chain colors for the wiring map
const C_NATURE = "#7ed6a0";
const C_WORK = "#e6b566";
const C_DEEP = "#9d7bf5";

const DoorIcon = ({ size = 16, stroke = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.7">
    <rect x="6" y="3" width="12" height="18" rx="2" />
    <circle cx="14.5" cy="12" r="1" fill={stroke} stroke="none" />
  </svg>
);

// --- The Eleven — nodes, wiring and shots (mirrors e2e/eleven-graph.js) -----
// The Venue has no card (see header) — it is a map node and a line of copy.
const FLOORS = [
  { id: "venue-classic", name: "The Venue", color: LIVE_DOT, x: 460, y: 560, img: null,
    tag: "The front door — arcade, TV lounge, concierge.",
    doors: ["The Gates → Portal Ring", "West door → the Speakeasy's alley", "East cellar door → the Descent surfaces here"] },
  { id: "portal-ring", name: "The Portal Ring", color: ACCENT, x: 460, y: 310, img: shotPortalRing,
    tag: "Twelve doors on a ring. Five are lit; seven stay sealed — sockets for rooms not built yet.",
    doors: ["North gate → Riverside Cascades", "East gate → Glass Boardroom", "South gate → the Venue", "West gate → Skyward Isles", "Deep gate → down, to Abyss Reef"] },
  { id: "riverside-cascades", name: "Riverside Cascades", color: C_NATURE, x: 470, y: 90, img: shotCascades,
    tag: "Waterfalls off the escarpment, fire circle on the bank.",
    doors: ["Falls cave → Portal Ring", "Ferry → Santorini Steps", "Moss gate → Sakura Garden"] },
  { id: "santorini", name: "Santorini Steps", color: C_NATURE, x: 190, y: 90, img: shotSantorini,
    tag: "White terraces over the caldera at sunset.",
    doors: ["The ferry → Riverside Cascades", "Blue gate → Skyward Isles"] },
  { id: "skyward-isles", name: "Skyward Isles", color: C_NATURE, x: 130, y: 300, img: shotSkyward,
    tag: "Floating islands, rope bridges, a balloon between them.",
    doors: ["The updraft → Portal Ring", "The balloon → Santorini Steps", "Anchor stone → Sakura Garden"] },
  { id: "sakura-garden", name: "Sakura Garden", color: C_NATURE, x: 300, y: 195, img: shotSakura,
    tag: "Moonlit blossom, a koi pond, a torii by the water.",
    doors: ["Torii → Skyward Isles", "Tea room → Riverside Cascades"] },
  { id: "glass-boardroom", name: "Glass Boardroom", color: C_WORK, x: 790, y: 290, img: shotBoardroom,
    tag: "A table above the city lights.",
    doors: ["The lobby → Portal Ring", "Breakout → the Reading Room"] },
  { id: "library-stacks", name: "The Reading Room", color: C_WORK, x: 810, y: 455, img: shotLibrary,
    tag: "Lamplight and tall stacks. In the closed stacks, pull the red book.",
    doors: ["Front desk → Glass Boardroom", "Closed stacks → the Speakeasy"] },
  { id: "speakeasy", name: "The Speakeasy", color: C_WORK, x: 640, y: 525, img: shotSpeakeasy,
    tag: "Behind the bookshelf: a bar, candlelight, a piano.",
    doors: ["The stacks → the Reading Room", "Alley door → the Venue"] },
  { id: "abyss-reef", name: "Abyss Reef", color: C_DEEP, x: 195, y: 475, img: shotAbyss,
    tag: "Through the deep gate — a moon pool on the sea floor.",
    doors: ["Moon pool → Portal Ring", "The trench → Crystal Caverns"] },
  { id: "crystal-caverns", name: "Crystal Caverns", color: C_DEEP, x: 340, y: 590, img: shotCaverns,
    tag: "A glow-lit lake deep under the world.",
    doors: ["Deep road → Abyss Reef", "Cold tunnel → surfaces in the Venue's cellar"] },
];

const LINKS = [
  ["portal-ring", "riverside-cascades"], ["portal-ring", "glass-boardroom"],
  ["portal-ring", "venue-classic"], ["portal-ring", "skyward-isles"],
  ["portal-ring", "abyss-reef"],
  ["riverside-cascades", "santorini"], ["santorini", "skyward-isles"],
  ["skyward-isles", "sakura-garden"], ["sakura-garden", "riverside-cascades"],
  ["glass-boardroom", "library-stacks"], ["library-stacks", "speakeasy"],
  ["speakeasy", "venue-classic"],
  ["abyss-reef", "crystal-caverns"], ["crystal-caverns", "venue-classic"],
];

const floorById = (id) => FLOORS.find((f) => f.id === id);

// the gallery — the destinations first, best foot forward; the hub and the
// wiring come after, once the reader has seen where the doors go
const CHAPTERS = [
  { title: "The nature loop", line:
      "Four floors that loop into each other. Take the ferry, the balloon, the torii and the tea room and you come out where you started.",
    floors: ["riverside-cascades", "santorini", "skyward-isles", "sakura-garden"] },
  { title: "The long walk", line:
      "The working side of the world: a boardroom over the city, a reading room off its breakout — and a red book in the closed stacks that opens a bar.",
    floors: ["glass-boardroom", "library-stacks", "speakeasy"] },
  { title: "The Descent", line:
      "Through the deep gate and down. A moon pool on the sea floor, a trench, a cavern lake — and a cold tunnel that surfaces back at the front door.",
    floors: ["abyss-reef", "crystal-caverns"] },
];

const Label = ({ children }) => (
  <span style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>
    {children}
  </span>
);

function WiringMap({ onPick }) {
  return (
    <svg viewBox="40 10 870 660" style={{ width: "100%", height: "auto", display: "block" }}
         role="img" aria-label="Map of the eleven connected floors">
      {LINKS.map(([a, b]) => {
        const fa = floorById(a), fb = floorById(b);
        return (
          <line key={a + b} x1={fa.x} y1={fa.y} x2={fb.x} y2={fb.y}
                stroke="rgba(116,160,245,0.28)" strokeWidth="2" />
        );
      })}
      {FLOORS.map((f) => (
        <g key={f.id} onClick={() => onPick(f.id)} style={{ cursor: "pointer" }}>
          <circle cx={f.x} cy={f.y} r="26" fill="transparent" />
          <circle cx={f.x} cy={f.y} r="15" fill={f.color} opacity="0.16">
            {f.id === "venue-classic" && (
              <animate attributeName="r" values="13;19;13" dur="2.4s" repeatCount="indefinite" />
            )}
          </circle>
          <circle cx={f.x} cy={f.y} r="6.5" fill={f.color} />
          <text x={f.x} y={f.y + 32} textAnchor="middle"
                style={{ fontFamily: MONO, fontSize: "15px", letterSpacing: "0.08em",
                         fill: "#b6b9c0", textTransform: "uppercase" }}>
            {f.name}
          </text>
        </g>
      ))}
    </svg>
  );
}

function FloorCard({ floor, wide, highlighted }) {
  return (
    <figure id={`wcf-${floor.id}`} className={wide ? "ga-floor-wide" : undefined}
      style={{ margin: 0, background: "#121419", border: "1px solid rgba(255,255,255,0.1)",
               borderRadius: "18px", overflow: "hidden",
               boxShadow: highlighted ? `0 0 0 2px ${floor.color}` : "none",
               transition: "box-shadow .3s" }}>
      <div style={{ aspectRatio: wide ? "2/1" : "3/2", overflow: "hidden" }}>
        <img src={floor.img} alt={`${floor.name} — one of the connected Water Cooler floors`}
             loading="lazy"
             style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      </div>
      <figcaption style={{ padding: "16px 20px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: floor.color, flex: "none" }} />
          <span style={{ fontFamily: SERIF, fontSize: "21px", letterSpacing: "-0.01em" }}>{floor.name}</span>
        </div>
        <p style={{ fontSize: "14px", lineHeight: 1.55, color: "#b6b9c0", margin: "8px 0 0" }}>{floor.tag}</p>
        <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "4px" }}>
          {floor.doors.map((d) => (
            <span key={d} style={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.06em", color: "#6b6f78" }}>
              ⌂ {d}
            </span>
          ))}
        </div>
      </figcaption>
    </figure>
  );
}

export default function WaterCoolerStory() {
  const navigate = useNavigate();
  const [activeCount, setActiveCount] = useState(0);
  const [highlightId, setHighlightId] = useState(null);
  const highlightTimer = useRef(null);

  useEffect(() => {
    api.get(API_PATHS.PUBLIC.WATERCOOLER_ACTIVITY)
      .then(({ data }) => setActiveCount(data.active_count || 0))
      .catch(() => {});
    return () => clearTimeout(highlightTimer.current);
  }, []);

  const liveLabel = activeCount > 0
    ? `${activeCount} HERE IN THE LAST 10 MIN`
    : "ALWAYS OPEN";

  const goHome = (e) => { e.preventDefault(); navigate("/"); };
  const enterWaterCooler = (e) => {
    e.preventDefault();
    navigate("/watercooler", { state: { from: "/watercooler-story" } });
  };
  const pickFloor = (id) => {
    setHighlightId(id);
    clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightId(null), 2600);
    // the Venue has no card; its id sits on the front-door paragraph
    const el = document.getElementById(`wcf-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div style={{ background: "#0c0d10", color: "#f1f2f4", minHeight: "100vh",
                  fontFamily: "'Hanken Grotesk',system-ui,sans-serif",
                  WebkitFontSmoothing: "antialiased" }}>
      {/* no overflow-x:hidden on this root: it would make the root the
          sticky bar's scroll container and the bar would scroll away. The
          map is the only wide thing and it scrolls inside .ga-mapwrap. */}
      <style>{`
        @keyframes ga-pulse { 0% { box-shadow: 0 0 0 0 rgba(87,217,164,0.5); } 70% { box-shadow: 0 0 0 7px rgba(87,217,164,0); } 100% { box-shadow: 0 0 0 0 rgba(87,217,164,0); } }
        .ga-link { color:#b6b9c0; text-decoration:none; transition:color .15s; }
        .ga-link:hover { color:#fff; }
        .ga-btn { transition:background .15s, border-color .15s; }
        .ga-btn-primary:hover { background:${ACCENT_STRONG} !important; }
        .ga-topbar { position:sticky; top:0; z-index:20; background:rgba(12,13,16,0.86);
                     backdrop-filter:blur(12px); -webkit-backdrop-filter:blur(12px);
                     border-bottom:1px solid rgba(255,255,255,0.08); }
        .ga-floor-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:22px; }
        .ga-floor-wide { grid-column:1 / -1; }
        .ga-mapwrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
        @media (max-width: 840px) {
          .ga-hero { grid-template-columns:1fr !important; gap:22px !important; }
          .ga-floor-grid { grid-template-columns:1fr; }
          .ga-mapwrap svg { min-width:680px; }
        }
        @media (max-width: 520px) {
          .ga-topbar-tag { display:none; }
          .ga-topbar-inner { padding:12px 18px !important; }
          .ga-enter { font-size:13px !important; padding:10px 14px !important; }
          .ga-pad { padding-left:18px !important; padding-right:18px !important; }
        }
      `}</style>

      {/* the one door on the page: sticky, so it's a glance away from any
          floor instead of at the top and again at the bottom */}
      <nav className="ga-topbar">
        <div className="ga-topbar-inner" style={{ maxWidth: "1140px", margin: "0 auto", padding: "16px 32px",
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
          <a href="/" onClick={goHome} style={{ textDecoration: "none", color: "inherit", display: "flex", alignItems: "baseline", gap: "12px", minWidth: 0 }}>
            <span style={{ fontFamily: SERIF, fontSize: "25px", letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>
              glonet<span style={{ color: ACCENT }}>.ai</span>
            </span>
            <span className="ga-topbar-tag" style={{ fontFamily: MONO, fontSize: "10px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>
              Global&nbsp;Network
            </span>
          </a>
          <a href="/watercooler" onClick={enterWaterCooler} className="ga-btn ga-btn-primary ga-enter"
             style={{ display: "inline-flex", alignItems: "center", gap: "9px", fontSize: "14px", flex: "none",
                      fontWeight: 600, color: "#0c0d10", background: ACCENT, padding: "11px 18px",
                      borderRadius: "999px", textDecoration: "none", whiteSpace: "nowrap" }}>
            <DoorIcon size={15} />
            Enter the Water Cooler
          </a>
        </div>
      </nav>

      {/* hero — short, no picture of its own: the floors below are the
          picture. Headline left, the one paragraph beside it, so the first
          floors are in the first viewport. */}
      <section className="ga-pad ga-hero" style={{ maxWidth: "1140px", margin: "0 auto", padding: "56px 32px 8px",
                                                   display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px 64px", alignItems: "end" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Label>The Water Cooler</Label>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", fontFamily: MONO, fontSize: "11px", letterSpacing: "0.14em", color: LIVE_DOT, border: "1px solid rgba(255,255,255,0.12)", borderRadius: "999px", padding: "4px 10px" }}>
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: LIVE_DOT, animation: "ga-pulse 1.8s infinite" }} />
              {liveLabel}
            </span>
          </div>
          <h1 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(34px,4.2vw,56px)", lineHeight: 1.04, letterSpacing: "-0.01em", margin: "18px 0 0", maxWidth: "16ch" }}>
            A world that's always open.
          </h1>
        </div>
        <p style={{ fontSize: "18px", lineHeight: 1.6, color: "#b6b9c0", margin: 0, maxWidth: "48ch" }}>
          When there's a match, the meeting already has a place. One always-on space for the whole nexus —
          walk over and the conversation just starts, voice and video, no links and no scheduling.
          Eleven floors, wired together behind real doors. Pick one.
        </p>
      </section>

      {/* the floors, best foot forward */}
      {CHAPTERS.map((ch) => (
        <section key={ch.title} className="ga-pad" style={{ maxWidth: "1140px", margin: "0 auto", padding: "44px 32px 10px" }}>
          <Label>{ch.title}</Label>
          <p style={{ fontSize: "16px", lineHeight: 1.6, color: "#b6b9c0", margin: "12px 0 22px", maxWidth: "66ch" }}>{ch.line}</p>
          <div className="ga-floor-grid">
            {ch.floors.map((id, i) => (
              <FloorCard key={id} floor={floorById(id)}
                         wide={ch.floors.length % 2 === 1 && i === ch.floors.length - 1}
                         highlighted={highlightId === id} />
            ))}
          </div>
        </section>
      ))}

      {/* the hub and the wiring — how you get between them */}
      <section className="ga-pad" style={{ maxWidth: "1140px", margin: "0 auto", padding: "72px 32px 10px" }}>
        <Label>Eleven floors, one wiring</Label>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(26px,3vw,40px)", lineHeight: 1.1, letterSpacing: "-0.01em", margin: "14px 0 0", maxWidth: "24ch" }}>
          Walk through a door, come out somewhere else.
        </h2>
        <p id="wcf-venue-classic" style={{ fontSize: "17px", lineHeight: 1.65, color: "#b6b9c0", margin: "18px 0 26px", maxWidth: "62ch" }}>
          Everyone arrives at the Venue — the hall with the arcade, the TV lounge and the concierge. Through its
          Gates is the Portal Ring, and every lit gate on the ring is a live door to one of the floors above.
          Take the west gate and you're standing on a floating island. No menus, no room codes. You walk.
        </p>
        <FloorCard floor={floorById("portal-ring")} wide highlighted={highlightId === "portal-ring"} />
        <div style={{ marginTop: "22px", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "18px",
                      background: "#0f1116", padding: "26px 18px 12px" }}>
          <div className="ga-mapwrap"><WiringMap onPick={pickFloor} /></div>
          <p style={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.14em", color: "#6b6f78",
                      textTransform: "uppercase", textAlign: "center", margin: "6px 0 10px" }}>
            The live wiring — every line is a real pair of doors. Tap a floor to see it
          </p>
        </div>
      </section>

      {/* sign-off; the door is in the bar above */}
      <section className="ga-pad" style={{ maxWidth: "1140px", margin: "0 auto", padding: "72px 32px 104px", textAlign: "center" }}>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(26px,3vw,40px)", letterSpacing: "-0.01em", margin: 0 }}>
          Every door is open right now.
        </h2>
        <p style={{ fontSize: "16px", lineHeight: 1.6, color: "#b6b9c0", margin: "14px auto 0", maxWidth: "52ch" }}>
          Start at the Venue, walk the whole world, and come back in through the cellar.
        </p>
      </section>
    </div>
  );
}
